import { v4 as uuid } from 'uuid';
import Tool from "./Tool";
import Vector2 from "./Vector2";
import Rect from "./Rect";
import MouseEventArgs, { MouseButton } from "./MouseEventArgs";
import Editor from "./Editor";
import Layer from "./Layer";
import ConnectionLayer from "./ConnectionLayer";
import NodeLayer from "./NodeLayer";
import { isDirectedCyclic } from "./GraphUtil";
import { createTransformToolChain } from "./toolChains";

export default class ConnectNodeTool extends Tool {
  private _dragging = false;
  private _startLayer: NodeLayer;
  private _endLayer: NodeLayer;

  private _connection: ConnectionLayer;

  public constructor(editor: Editor) {
    super(editor, "connect_node");
  }

  public handleMouseDown(arg: MouseEventArgs): boolean {
    if (arg.button != MouseButton.Left) {
      return false;
    }

    var pos = arg.worldPosition;
    var selectedLayer = this.editor.rootLayerGroup.checkPointIntersection(pos);

    if (selectedLayer != null && selectedLayer instanceof NodeLayer) {
      this.editor.setSelectedLayer(null);

      this._startLayer = selectedLayer as NodeLayer;
      this._dragging = true;
      this._connection = new ConnectionLayer(
        this.editor,
        uuid(),
        this._startLayer
      );
      this.editor.rootLayerGroup.addLayer(this._connection);

      return true;
    }

    return false;
  }

  public handleMouseMove(arg: MouseEventArgs): boolean {
    if (this._dragging == false) {
      return false;
    }

    var pos = arg.worldPosition;
    var selectedLayer = this.editor.rootLayerGroup.checkPointIntersection(pos);

    this._endLayer = selectedLayer instanceof NodeLayer ? selectedLayer : null;

    this._connection.setEndPoint(pos);
    return true;
  }

  private checkRemovalNeeded(selectedLayer: Layer): boolean {
    if (selectedLayer == null) {
      return true;
    }

    var startNode = this._connection.startNode;

    if (selectedLayer == startNode) {
      return true;
    }

    for (var conn of startNode.outgoingConnections) {
      if (conn.endNode == selectedLayer) {
        return true;
      }
    }

    return false;
  }

  public handleMouseUp(arg: MouseEventArgs): boolean {
    if (this._dragging == false) {
      return false;
    }
    this._dragging = false;
    this._endLayer = null;

    var pos = arg.worldPosition;
    var selectedLayer = this.editor.rootLayerGroup.checkPointIntersection(pos);
    selectedLayer = selectedLayer instanceof NodeLayer ? selectedLayer : null;

    var connectionAdded = true;

    if (this.checkRemovalNeeded(selectedLayer)) {
      this.editor.rootLayerGroup.removeLayer(this._connection);
      connectionAdded = false;
    } else {
      this._connection.setEndNode(selectedLayer as NodeLayer);
      this._connection.startNode.addOutgoingConnection(this._connection);
      this._connection.endNode.addIncomingConnection(this._connection);

      if (isDirectedCyclic(this._connection.startNode)) {
        this._connection.startNode.removeOutgoingConnection(this._connection);
        this._connection.endNode.removeIncomingConnection(this._connection);
        this.editor.rootLayerGroup.removeLayer(this._connection);

        this.editor.setError({
          title: "OOPS!",
          value: "Can not create cyclic connections. Please try again.",
        });

        connectionAdded = false;
      }
    }

    if (connectionAdded) {
      this.editor.setSelectedLayer(this._connection);
      this.editor.setCurrentTool(createTransformToolChain(this.editor));
    }

    this._connection = null;
    return true;
  }

  public draw(context: CanvasRenderingContext2D) {
    var selectedLayer = this._endLayer;
    if (selectedLayer == null) {
      return;
    }

    var blue = "rgb(155, 66, 227)";

    context.strokeStyle = blue;
    context.lineWidth = 2 * window.devicePixelRatio;

    var rect = this.editor.camera.transformRectWorldToScreen(
      selectedLayer.boundingRect
    );

    var p0 = rect.position;
    var p1 = rect.position.add(new Vector2(rect.size.x, 0));
    var p2 = rect.position.add(rect.size);
    var p3 = rect.position.add(new Vector2(0, rect.size.y));

    context.beginPath();
    context.moveTo(p0.x, p0.y);
    context.lineTo(p1.x, p1.y);
    context.lineTo(p2.x, p2.y);
    context.lineTo(p3.x, p3.y);
    context.closePath();
    context.stroke();
  }
}
